import { getLocalToken, clearLocalToken } from "./localAuth";

// Single token choke-point: in dev-only LOCAL_AUTH mode a token is stored in
// localStorage and used directly; otherwise fall back to Auth0's
// getAccessTokenSilently. This lets every existing call site keep passing
// getAccessTokenSilently untouched.
function resolveToken(retrieveAccessToken) {
    const local = getLocalToken();
    if (local) return Promise.resolve(local);
    return retrieveAccessToken();
}

// True when the API rejected the token. The backend returns
// { message: "JWT is invalid." } for an expired/invalid token.
export function isAuthFailureResponse(data) {
    return data && typeof data === "object" && data.message === "JWT is invalid.";
}

// Token is bad (expired/invalid): drop it and send the user to log in again,
// instead of leaving them on a screen whose every call 401s.
function redirectToLogin() {
    clearLocalToken();
    if (window.location.pathname !== "/login") {
        window.location.assign("/login");
    }
}

// Parse a response body as JSON when possible, else as plain text — backend
// handlers return JSON on success but plain-text `http.Error` bodies on failure.
// Returns { ok, status, data } so callers can branch on HTTP status, not just
// on the body (which the old helpers ignored, treating 400s as success).
export async function parseApiResponse(response) {
    const text = await response.text();
    let data;
    try {
        data = text ? JSON.parse(text) : {};
    } catch (e) {
        data = { message: text };
    }
    return { ok: response.ok, status: response.status, data };
}

// Best-effort human-readable message from an error response body.
export function apiErrorMessage(data, status) {
    if (data && typeof data === "object" && data.message) {
        return data.message;
    }
    if (typeof data === "string" && data) {
        return data;
    }
    return "Request failed (" + status + ")";
}

export function FetchApi(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler) {
    (async () => {
        await resolveToken(retrieveAccessToken).then((token) => {
            fetch(url, {
                headers: {
                    Authorization: `Bearer ` + token,
                }
            }).then((response) => response.json()).then((data) => {
                if (isAuthFailureResponse(data)) {
                    redirectToLogin();
                } else {
                    successHandler(data);
                }
            }).catch((err) => {
                failureHandler(err);
            })
        }).catch((response) => {
            authFailureHandler(response);
        });
    })();
}

export function FetchApiPromise(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler) {
    return resolveToken(retrieveAccessToken).then((token) => {
        fetch(url, {
            headers: {
                Authorization: `Bearer ` + token,
            }
        }).then((response) => response.json()).then((data) => {
            if (isAuthFailureResponse(data)) {
                redirectToLogin();
            } else {
                successHandler(data);
            }
        }).catch((err) => {
            failureHandler(err);
        })
    }).catch((response) => {
        authFailureHandler(response);
    });
}

export function SaveApi(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate) {
    return modifyRestCall("PATCH", retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate)
}

export function OverwriteApi(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate) {
    return modifyRestCall("PUT", retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate)
}

export function PostApi(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate) {
    return modifyRestCall("POST", retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate)
}

export function DeleteApi(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate) {
    return modifyRestCall("DELETE", retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate)
}

function modifyRestCall(methodType, retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, recordUpdate) {
    (async () => {
        await resolveToken(retrieveAccessToken).then((token) => {
            fetch(url, {
                headers: {
                    Authorization: `Bearer ` + token,
                },
                method: methodType,
                body: JSON.stringify(recordUpdate)
            }).then(parseApiResponse)
                .then(({ ok, status, data }) => {
                    if (status === 401 || isAuthFailureResponse(data)) {
                        redirectToLogin();
                    } else if (!ok) {
                        failureHandler(new Error(apiErrorMessage(data, status)));
                    } else {
                        successHandler(data);
                    }
                }).catch((err) => {
                failureHandler(err);
            })
        }).catch((response) => {
            authFailureHandler(response);
        });
    })();
}

// fields: extra form values sent with the file (all strings).
export function UploadFileApi(retrieveAccessToken, url, successHandler, authFailureHandler, failureHandler, file, fields) {
    (async () => {
        await resolveToken(retrieveAccessToken).then((token) => {
            const data = new FormData();
            data.append('uploadedFile', file);
            Object.entries(fields || {}).forEach(([k, v]) => data.append(k, v));

            fetch(url, {
                headers: {
                    Authorization: `Bearer ` + token,
                },
                method: "POST",
                body: data,
            }).then(parseApiResponse)
                .then(({ ok, status, data }) => {
                    if (status === 401 || isAuthFailureResponse(data)) {
                        redirectToLogin();
                    } else if (!ok) {
                        failureHandler(new Error(apiErrorMessage(data, status)));
                    } else {
                        successHandler(data);
                    }
                }).catch((err) => {
                failureHandler(err);
            })
        }).catch((response) => {
            authFailureHandler(response);
        });
    })();

}

// DownloadApi fetches a binary/file response with the auth token and triggers a
// browser download (a plain <a href> can't send the Bearer header).
export function DownloadApi(retrieveAccessToken, url, filename, onError) {
    resolveToken(retrieveAccessToken).then((token) => {
        fetch(url, { headers: { Authorization: `Bearer ` + token } })
            .then((response) => {
                if (response.status === 401) {
                    redirectToLogin();
                    return null;
                }
                if (!response.ok) {
                    throw new Error("Download failed (" + response.status + ")");
                }
                return response.blob();
            })
            .then((blob) => {
                if (!blob) return;
                const objUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = objUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(objUrl);
            })
            .catch((err) => onError && onError(err));
    }).catch((err) => onError && onError(err));
}